import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { map, startWith, take, takeUntil } from 'rxjs/operators';

import { AvailableLanguages } from '@constants';
import { AllDeviceInfo, ConsumableCountry } from '@interfaces';
import { CommonsFacade } from '@state/commons';
import { ReplaySubject } from 'rxjs';
import { DeviceInfoService } from '@core/services/device-info.service';
import { LayoutService } from '@layout/layout.service';

export interface CountrySelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-country-select',
  templateUrl: './country-select.component.html',
  styleUrls: ['./country-select.component.scss'],
  standalone: false,
})
export class CountrySelectComponent implements OnInit, OnDestroy {
  private readonly commonsFacade = inject(CommonsFacade);
  private readonly translateService = inject(TranslateService);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly layoutService = inject(LayoutService);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @ViewChild('countrySelect') countrySelect: ElementRef;

  @Input() fieldControl: FormControl;
  @Input() fieldKey: string;
  @Input() fieldLabelKey: string;
  @Input() isRequired: boolean;
  // Array of pseudoId -> you can find those in api -> modules -> common -> countries.ts
  @Input() filterOutIds?: number[];
  @Input() onlyUE?: boolean;
  @Input()
  public set minWidth(value: number | string | null | undefined) {
    this.minWidthCssVariable = this.normalizeCssSize(value);
  }

  public deviceInfo: AllDeviceInfo;
  public minWidthCssVariable: string | null = null;

  public countriesList: CountrySelectOption[];
  public currentLang: string;
  private rawCountries: ConsumableCountry[];

  private normalizeCssSize(value: number | string | null | undefined): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    return typeof value === 'number' ? `${value}px` : value;
  }

  public async ngOnInit(): Promise<void> {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.currentLang = this.translateService.currentLang;
    if (this.onlyUE) {
      this.rawCountries = await this.commonsFacade
        .getCountriesEU$()
        .pipe(
          take(1),
          map(countriesEU => {
            if (this.filterOutIds?.length) {
              return countriesEU.filter(countriesEU => !this.filterOutIds.includes(countriesEU.id));
            } else {
              return countriesEU;
            }
          })
        )
        .toPromise();
    } else {
      this.rawCountries = await this.commonsFacade
        .getCountries$()
        .pipe(
          take(1),
          map(countries => {
            if (this.filterOutIds?.length) {
              return countries.filter(country => !this.filterOutIds.includes(country.id));
            } else {
              return countries;
            }
          })
        )
        .toPromise();
    }

    this.translateService.onLangChange
      .pipe(startWith({ lang: this.currentLang }), takeUntil(this.destroy$))
      .subscribe(({ lang: langKey }) => {
        const langKeyTyped = langKey as AvailableLanguages;
        const countriesToUse = this.rawCountries.map(consumableCountry => ({
          value: consumableCountry.name,
          label: consumableCountry.nameTranslations[langKeyTyped],
        }));
        countriesToUse.sort((a, b) => a.label.localeCompare(b.label));
        this.countriesList = countriesToUse;
        this.currentLang = langKey;
      });
  }

  public ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public onOpen(): void {
    if (this.deviceInfo.deviceTypeDetected !== 'DESKTOP') {
      setTimeout(() => {
        const container = document.querySelector('.content');
        const target = this.countrySelect.nativeElement;

        const containerTop = container.getBoundingClientRect().top;
        const targetTop = target.getBoundingClientRect().top;

        this.layoutService.scrollContentTo$.next({
          top: container.scrollTop + (targetTop - containerTop - 25),
          behavior: 'smooth',
        });
      }, 200);
    }
  }
}
